import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Icon } from 'Components/atoms/Icon';
import { Image } from 'Components/atoms/Image';
import { Price } from 'Components/atoms/Price';
import { Link } from 'Components/molecules/Link';
import { withPolaris } from 'Components/protons/Polaris';
import { asyncTrackLinkClick } from 'SharedHelpers/tracking';

import './_style.scss';

/**
 * LeasingCarDealsCard component renders a card with product information including image, title, price, and links.
 */
const LeasingCarDealsCard = ({
  context: { cssPrefix },
  extraClassNames,
  icon,
  image: {
    alt,
    height,
    src,
    width,
  },
  marketplaceLink: {
    label: marketplaceLabel,
    tracking: marketplaceTracking,
    url: marketplaceUrl,
  },
  financePrice: {
    min: financePrice,
    prefixText: financePrefixText,
    postText: financePostText,
  },
  title,
}) => {
  /**
   * Handles the link click events.
   * Pushes the tracking object to the dataLayer.
   * @example of tracking object:
   * {
   *  event: 'marketplace.button_click',
   *  'event.type': 'GYCCustom',
   *  'marketplace_button.placement': 'GYCHotDealsBlockCustom',
   *  }
   */
  const handleLinkClick = async (trackingPayload) => {
    if (typeof window !== 'undefined' && trackingPayload && Object.keys(trackingPayload).length > 0) {
      window.dataLayer = window.dataLayer || [];
      await asyncTrackLinkClick(trackingPayload);
    }
  };

  return (
    <div
      className={classNames({
        [`${cssPrefix}__leasing-car-deals-card`]: true,
        ...extraClassNames,
      })}
    >
      <div className={`${cssPrefix}__leasing-car-deals-card--media`}>
        {src && ( // Only render the image if the src is provided, but keep the container for layout purposes
          <Image
            extraClassNames={{
              [`${cssPrefix}__leasing-car-deals-card--image`]: true,
            }}
            disableTransforms
            src={src}
            alt={alt}
            height={height > 0 ? height : undefined}
            width={width > 0 ? width : undefined}
          />
        )}
      </div>
      <div className={`${cssPrefix}__leasing-car-deals-card--content`}>
        <p className={`${cssPrefix}__leasing-car-deals-card--title`}>{title}</p>
        <div className={`${cssPrefix}__leasing-car-deals-card--ctas`}>
          {marketplaceUrl && marketplaceLabel && (
            <Link
              extraClassNames={{
                [`${cssPrefix}__leasing-car-deals-card--link`]: true,
              }}
              href={marketplaceUrl}
              loadStyles={false}
              onClick={async () => {
                if (marketplaceTracking) {
                  await handleLinkClick(marketplaceTracking);
                }
              }}
            >
              <div className={`${cssPrefix}__leasing-car-deals-card--link-label`}>
                {marketplaceLabel}
                {financePrice > 0 && (
                  <Price
                    extraClassNames={{
                      [`${cssPrefix}__leasing-car-deals-card--price`]: true,
                    }}
                    minOnly
                    min={financePrice}
                    prefixText={financePrefixText}
                    postText={financePostText}
                  />
                )}
              </div>
              {icon && (
              <div className={`${cssPrefix}__leasing-car-deals-card--link-icon`}>
                <Icon icon={icon} />
              </div>
              )}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

LeasingCarDealsCard.propTypes = {
  financePrice: PropTypes.shape({
    min: PropTypes.number,
    prefixText: PropTypes.string,
    postText: PropTypes.string,
  }),
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  extraClassNames: PropTypes.shape({}),
  icon: PropTypes.string,
  image: PropTypes.shape({
    alt: PropTypes.string,
    height: PropTypes.number,
    src: PropTypes.string,
    width: PropTypes.number,
  }),
  marketplaceLink: PropTypes.shape({
    label: PropTypes.string,
    tracking: PropTypes.shape(),
    url: PropTypes.string,
  }),
  title: PropTypes.string,
};

LeasingCarDealsCard.defaultProps = {
  financePrice: {
    min: 0,
    prefixText: '',
    postText: '',
  },
  context: { cssPrefix: 'polaris' },
  extraClassNames: {},
  icon: null,
  image: {
    alt: '',
    height: 0,
    src: '',
    width: 0,
  },
  marketplaceLink: {
    label: '',
    tracking: {},
    url: '',
  },
  title: '',
};

export default withPolaris(LeasingCarDealsCard);
